<script lang="ts">
import { defineComponent, PropType } from 'vue'
import DateDefault from '@/components/date/DateDefault.vue'
import { dateFormatJa2 } from '@/utilities/formatter'

export default defineComponent({
  name: 'TimelineUserName',

  components: {
    DateDefault,
  },

  props: {
    /**
     * ユーザー名
     */
    name: {
      type: String,
      required: true,
    },

    /**
     * プロフィール画像の URL
     */
    avatarUrl: {
      type: String,
      required: true,
    },

    /**
     * 会社名
     */
    companyName: {
      type: String,
      default: '',
    },

    /**
     * 役職名
     */
    departmentAndPosition: {
      type: String,
      default: '',
    },

    /**
     * タイムスタンプ
     */
    date: {
      type: String,
      default: ''
    },

    size: {
      type: String as PropType<'small' | 'normal' | 'large'>,
      default: 'normal',
      validator: (value: string) => {
        return ['small', 'normal', 'large'].includes(value)
      },
    },
  },

  emits: ['mouseover:item', 'mouseout:item', 'click:item'],

  setup() {
    return {
      // methods
      dateFormatJa2,
    }
  }
})
</script>

<template>
  <div
    :class="[
      'l-unit__timelineUser',
      `is-${size}`,
    ]">
    <!-- ユーザー名 -->
    <div class="l-unit__timelineUser--header">
      <div
        :class="[
          'o-label__name',
          `is-${size}`,
        ]"
        @mouseover="$emit('mouseover:item', $event)"
        @mouseout="$emit('mouseout:item')"
        @click="$emit('click:item')">
        <p class="o-label__name--item">
          {{ name }}
        </p>
      <!-- /.o-label__name --></div>
    <!-- /.l-unit__name header --></div>

    <!-- ユーザーアバター画像 -->
    <div
      class="l-unit__timelineUser--image"
      @mouseover="$emit('mouseover:item', $event)"
      @mouseout="$emit('mouseout:item')"
      @click="$emit('click:item')">
      <img
        v-lazy="avatarUrl"
        class="img"
        alt=""
        data-is-liquid="true">
    <!-- /.l-unit__timelineUser image --></div>

    <div
      v-if="companyName"
      class="l-unit__timelineUser--description is-companyName">
      <div
        :class="[
          'o-label__userCareer',
          `is-${size}`,
        ]"
        @mouseover="$emit('mouseover:item', $event)"
        @mouseout="$emit('mouseout:item')"
        @click="$emit('click:item')">
        <p class="o-label__userCareer--item">
          {{ companyName }}
        </p>
      <!-- /.o-label__userCareer --></div>
    <!-- /.l-unit__timelineUser description --></div>

    <div
      v-if="departmentAndPosition"
      class="l-unit__timelineUser--description is-departmentAndPosition">
      <div
        :class="[
          'o-label__userCareer',
          `is-${size}`,
        ]"
        @mouseover="$emit('mouseover:item', $event)"
        @mouseout="$emit('mouseout:item')"
        @click="$emit('click:item')">
        <p class="o-label__userCareer--item">
          {{ departmentAndPosition }}
        </p>
      <!-- /.o-label__userCareer --></div>
    <!-- /.l-unit__timelineUser description --></div>

    <div
      v-if="date"
      class="l-unit__timelineUser--description is-date">
      <DateDefault
        :date="date"
        :size="size === 'large' ? 'large' : 'normal'"
        :format="dateFormatJa2(date)"
        truncated
        @mouseover="$emit('mouseover:item', $event)"
        @mouseout="$emit('mouseout:item')"
        @click="$emit('click:item')" />
    <!-- /.l-unit__timelineUser description --></div>
  <!-- /.l-unit__timelineUser --></div>
</template>

<style lang="sass" scoped>
.l-unit__timelineUser
  $root: &
  display: grid
  grid-template-rows: 1fr auto auto auto 1fr // the first and last item is used as a spacer.
  grid-template-columns: repeat(auto-fill, minmax(0, max-content))
  position: relative

  & &--header
    width: 100%
    grid-row: 2 / 3

  & &--image
    position: absolute
    top: 0
    left: 0
    overflow: hidden
    border-radius: 50%

  & &--description
    &.is-companyName,
    &.is-departmentAndPosition
      max-width: 100%
      grid-row: 3 / 4

    &.is-companyName
      margin-right: 0.8em

    &.is-date
      width: 100%
      grid-row: 4 / 5

  &.is-small
    $icon-size: 32
    $icon-margin: 8
    $image-area-size: $icon-size + $icon-margin
    min-height: changeSizePx2Em($icon-size)
    padding-left: changeSizePx2Em($image-area-size)

    #{$root}--image
      width: changeSizePx2Em($icon-size)

    #{$root}--description
      margin-top: changeSizePx2Em(4)

  &.is-normal
    $icon-size: 48
    $icon-margin: 12
    $image-area-size: $icon-size + $icon-margin
    min-height: changeSizePx2Em($icon-size)
    padding-left: changeSizePx2Em($image-area-size)

    #{$root}--image
      width: changeSizePx2Em($icon-size)

    #{$root}--description
      margin-top: changeSizePx2Em(8)

  &.is-large
    $icon-size: 100
    $icon-margin: 16
    $image-area-size: $icon-size + $icon-margin
    min-height: changeSizePx2Em($icon-size)
    grid-template-rows: 1fr auto auto auto auto 1fr // the first and last item is used as a spacer.
    grid-template-columns: minmax(0, max-content)
    padding-left: changeSizePx2Em($image-area-size)

    #{$root}--image
      width: changeSizePx2Em($icon-size)

    #{$root}--description
      margin-top: changeSizePx2Em(12)

      &.is-companyName
        grid-row: 3 / 4
        margin-right: 0

      &.is-departmentAndPosition
        grid-row: 4 / 5

      &.is-date
        width: 100%
        grid-row: 5 / 6

  +breakpoint
    &.is-large
      $icon-size: 80
      $icon-margin: 16
      $image-area-size: $icon-size + $icon-margin
      min-height: changeSizePx2Em($icon-size)
      padding-left: changeSizePx2Em($image-area-size)

      #{$root}--image
        width: changeSizePx2Em($icon-size)

      #{$root}--description
        margin-top: 0.5em

.o-label__name
  $root: &

  & &--item
    overflow: hidden
    line-height: 1.2
    white-space: nowrap
    text-overflow: ellipsis
    cursor: default

  &.is-small
    #{$root}--item
      font-size: $font-x-small

  &.is-normal
    #{$root}--item
      font:
        size: $font-small
        weight: bold

  &.is-large
    #{$root}--item
      font:
        size: $font-xx-large
        weight: bold

  +breakpoint
    &.is-large
      #{$root}--item
        font-size: $font-normal

.o-label__userCareer
  $root: &

  & &--item
    overflow: hidden
    line-height: 1.2
    color: $font-gray
    white-space: nowrap
    text-overflow: ellipsis
    cursor: default

  &.is-small,
  &.is-normal
    #{$root}--item
      font-size: $font-x-small

  &.is-large
    #{$root}--item
      font-size: $font-small

  +breakpoint
    &.is-large
      #{$root}--item
        font-size: $font-x-small
</style>
